import { React, PropTypes } from 'imports';
import hoistNonReactStatics from 'hoist-non-react-statics';
import moment from 'moment/src/moment';
// import en from 'moment/src/locale/en'; // included by default
import fi from 'moment/src/locale/fi';
import sv from 'moment/src/locale/sv';
import { omit } from 'utils';

// TODO find how to get needed data for current locale without bundling all of available datas
// or to remove moment - currently stringification is done by Intl, but it can not parse dates according to current locale, moment is for parsing only
// moment.updateLocale('en', en); // included by default
moment.updateLocale('fi', fi);
moment.updateLocale('sv', sv);

const FIRST_DAY_OF_WEEK = { // there is no such data in Intl
    'en': 0, //starting from Sunday
    'en-FI': 1,
    'fi': 1,
    'fi-FI': 1,
    'sv': 0,
    'sv-FI': 1,
};

const convertFormatOptionsToMomentFormat = function (locale, formatOptions) {
    return new Intl.DateTimeFormat(locale, formatOptions)
        .formatToParts(new Date(0))
        .map((part, _, parts) => {
            switch (part.type) {
                default:
                case 'literal': return part.value;
                case 'day': return formatOptions.day !== '2-digit' ? 'D' : 'DD';
                case 'dayPeriod': return 'A';
                case 'era': return '';
                case 'hour': return (formatOptions.hour !== '2-digit' ? 'H' : 'HH')[formatOptions.hour12 ? 'toLowerCase' : 'toUpperCase']();
                case 'minute': return formatOptions.minute !== '2-digit' ? 'm' : 'mm';
                case 'month':
                    switch (formatOptions.month) {
                        default:
                        case undefined: return 'M';
                        case 'numeric': return 'M';
                        case '2-digit': return 'MM';
                        case 'narrow': return 'MMM';
                        case 'short': return 'MMM';
                        case 'long': return 'MMMM';
                    }
                case 'second': return formatOptions.second !== '2-digit' ? 's' : 'ss';
                case 'timeZoneName': return formatOptions.timeZoneName === 'short' ? 'Z' : 'ZZ';
                case 'weekday':
                    switch (formatOptions.month) {
                        default:
                        case undefined: return 'ddd';
                        case 'narrow': return 'ddd';
                        case 'short': return 'ddd';
                        case 'long': return 'dddd';
                    }
                case 'year': return formatOptions.year !== '2-digit' ? 'YYYY' : 'YY';
            }
        })
        .join('');
}

export const dateLocaleUtilsPropTypes = {
    locale: PropTypes.string.isRequired,
    localeUtils: PropTypes.shape({
        formatDay: PropTypes.func.isRequired,
        formatMonthTitle: PropTypes.func.isRequired,
        formatWeekdayShort: PropTypes.func.isRequired,
        formatWeekdayLong: PropTypes.func.isRequired,
        getFirstDayOfWeek: PropTypes.func.isRequired,
        getMonths: PropTypes.func.isRequired
    }).isRequired,
    parseDate: PropTypes.func.isRequired,
    stringifyDate: PropTypes.func.isRequired,
    getDateFormat: PropTypes.func.isRequired,
    formatDayShort: PropTypes.func,
    formatWeekShort: PropTypes.func
};

export default function withDateLocaleUtils(WrappedComponent) {
    return hoistNonReactStatics(class withDateLocaleUtils extends React.PureComponent {
        static get dateLocaleUtilsPropTypes() {
            return dateLocaleUtilsPropTypes;
        }

        static get propTypes() {
            return {
                ...omit(WrappedComponent.propTypes, Object.keys(dateLocaleUtilsPropTypes)),
                formatOptions: PropTypes.object
            };
        }

        static get defaultProps() {
            return {
                ...WrappedComponent.defaultProps,
                formatOptions: { day: 'numeric', month: 'short', year: 'numeric' }
            }
        }

        static get contextTypes() {
            return {
                ...WrappedComponent.contextTypes,
                intl: PropTypes.object
            }
        }

        static get displayName() {
            return `withDateLocaleUtils(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
        }

        static get WrappedComponent() {
            return WrappedComponent;
        }

        localeUtils = {
            // implementation of http://react-day-picker.org/docs/utils-locale.html using Intl
            // all of function should accept locale, but it's controlled by context anyway, let's just use it directly
            formatDay: date => this.context.intl.formatDate(date, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }),
            formatMonthTitle: date => this.context.intl.formatDate(date, { month: 'long', year: 'numeric' }),
            formatWeekdayShort: dayIndex => {
                const a = 1;
                return this.context.intl.formatDate(new Date(Date.UTC(1, 6, dayIndex)) /*just a random month happened to start from Sunday*/, { weekday: 'short' })
            },
            formatWeekdayLong: dayIndex => this.context.intl.formatDate(new Date(Date.UTC(1, 6, dayIndex)) /*just a random month happened to start from Sunday*/, { weekday: 'long' }),
            getFirstDayOfWeek: () => FIRST_DAY_OF_WEEK[this.context.intl.locale] || 0,
            getMonths: () => Array.from(Array(12)).map((_, index) => this.context.intl.formatDate(Date.UTC(new Date(1, index, 1)), { month: 'long' }))
        }

        _formatDayShort = (date) => {
            return this.context.intl.formatDate(date, {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        }

        _formatWeekShort = (date) => {
            return this.context.intl.formatMessage({ id: 'common.period.week' }) + ' ' + date.getUTCWeek() + ', ' + date.getUTCFullYear();
        }

        _parseDate = (textValue) => {
            const result = moment(textValue, [
                convertFormatOptionsToMomentFormat(this.context.intl.locale, this.props.formatOptions),
                convertFormatOptionsToMomentFormat(this.context.intl.locale, {})
            ], this.context.intl.locale);
            if (result.isValid()) {
                return result.toDate();
            }
            else {
                return null;
            }
        }

        _stringifyDate = (value) => {
            return value ? this.context.intl.formatDate(value, this.props.formatOptions) : '';
        }

        _getDateFormat = () => {
            return convertFormatOptionsToMomentFormat(this.context.intl.locale, {});
        }

        render() {
            return (
                <WrappedComponent
                    locale={this.context.intl.locale}
                    localeUtils={this.localeUtils}
                    parseDate={this._parseDate}
                    stringifyDate={this._stringifyDate}
                    getDateFormat={this._getDateFormat}
                    formatDayShort={this._formatDayShort}
                    formatWeekShort={this._formatWeekShort}
                    {...this.props} />
            );
        }
    }, WrappedComponent);
}